// Turns user input (raw hex text, or guided-mode form values) into a byte array
// the ECU engine can process. Kept separate from decoding/engine logic per the
// tool's implementation notes, so raw-hex validation can be unit-tested/extended
// on its own.

export function parseRawHex(input) {
  const cleaned = (input || '').trim();
  if (!cleaned) return { bytes: null, error: 'Enter at least one byte, e.g. 22 F1 90.' };

  const stripped = cleaned.replace(/0[xX]/g, '').replace(/[\s,]+/g, '');
  if (!/^[0-9a-fA-F]*$/.test(stripped)) {
    return { bytes: null, error: 'Only hex digits (0-9, A-F), spaces, and commas are allowed.' };
  }
  if (stripped.length % 2 !== 0) {
    return { bytes: null, error: `Odd number of hex digits (${stripped.length}) — every byte needs two hex digits.` };
  }
  const bytes = [];
  for (let i = 0; i < stripped.length; i += 2) {
    bytes.push(parseInt(stripped.slice(i, i + 2), 16));
  }
  if (bytes.length === 0) return { bytes: null, error: 'Enter at least one byte, e.g. 22 F1 90.' };
  return { bytes, error: null };
}

export function did16ToBytes(id) {
  return [(id >> 8) & 0xff, id & 0xff];
}

export function bytesToDid16(hi, lo) {
  return ((hi & 0xff) << 8) | (lo & 0xff);
}

// Builds the raw request byte array from a guided-mode form's params object.
// `params` shape depends on `sid` — see each case below and the corresponding
// guided form fields in RequestBuilderPanel.jsx.
export function buildGuidedRequest(sid, params = {}) {
  switch (sid) {
    case 0x10:
      return [0x10, params.subFn ?? 0x01];

    case 0x11:
      return [0x11, params.subFn ?? 0x01];

    case 0x14:
      return [0x14, 0xff, 0xff, 0xff];

    case 0x19: {
      if (params.subFn === 0x04) {
        const dtc = params.dtcBytes || [0x00, 0x00, 0x00];
        return [0x19, 0x04, ...dtc, params.snapshotRecordNumber ?? 0x01];
      }
      return [0x19, 0x02, params.statusMask ?? 0xff];
    }

    case 0x22: {
      const did = params.did ?? 0xf190;
      return [0x22, ...did16ToBytes(did)];
    }

    case 0x27: {
      if (params.subFn === 0x02) {
        const key = params.keyBytes;
        if (!Array.isArray(key) || key.length !== 2) {
          throw new Error('Enter a 2-byte key (4 hex digits) before sending.');
        }
        return [0x27, 0x02, ...key];
      }
      return [0x27, 0x01];
    }

    case 0x2E: {
      const did = params.did ?? 0xf187;
      const data = params.dataBytes;
      if (!Array.isArray(data) || data.length === 0) {
        throw new Error('Enter at least one data byte to write.');
      }
      return [0x2e, ...did16ToBytes(did), ...data];
    }

    case 0x3E:
      return [0x3e, params.suppress ? 0x80 : 0x00];

    default:
      throw new Error(`No guided-mode builder for SID 0x${sid.toString(16)}.`);
  }
}

// Encodes a text value into `length` bytes for a DID's configured format, padding
// (ascii: spaces, numeric/hex: zero) or truncating to fit — mirrors how the ECU
// engine stores values, so the guided "Write Data By Identifier" preview matches
// what will actually be sent.
export function encodeDidValue(text, format, length) {
  if (format === 'ascii') {
    const padded = (text || '').slice(0, length).padEnd(length, ' ');
    return Array.from(padded, (c) => c.charCodeAt(0) & 0xff);
  }
  const cleaned = (text || '').replace(/[^0-9a-fA-F]/g, '');
  const bytes = [];
  for (let i = 0; i < length; i += 1) {
    const chunk = cleaned.slice(i * 2, i * 2 + 2).padStart(2, '0');
    bytes.push(parseInt(chunk, 16) || 0);
  }
  return bytes;
}
